import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Home, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validated email domains
const VALID_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'yahoo.in', 'yahoo.co.in', 'outlook.com', 'hotmail.com', 
  'live.com', 'icloud.com', 'protonmail.com', 'zoho.com', 'aol.com', 'mail.com',
  'ymail.com', 'rediffmail.com', 'msn.com', 'googlemail.com'
];

// 5 Deep Emotional Questions
const SOUL_QUESTIONS = [
  {
    id: 1,
    title: "The Mask",
    hindi: "Pehchan",
    question: "If we remove your name, your job title, and your social status... what is the one thing that still defines you?",
    keywords: ["name", "job title", "social status", "defines"],
    options: [
      { key: "a", text: "My Courage", subtext: "The strength to stand up again", element: "fire" },
      { key: "b", text: "My Kindness", subtext: "The urge to help others", element: "water" },
      { key: "c", text: "My Curiosity", subtext: "The hunger to learn and explore", element: "air" },
      { key: "d", text: "My Vision", subtext: "The ability to lead others", element: "earth" }
    ]
  },
  {
    id: 2,
    title: "The Inner Peace",
    hindi: "Sukoon",
    question: "In this busy world, where do you find your truest peace?",
    keywords: ["peace"],
    options: [
      { key: "a", text: "In creating something new", subtext: "Or thinking of ideas", element: "fire" },
      { key: "b", text: "In a deep, honest talk", subtext: "With a stranger", element: "water" },
      { key: "c", text: "In complete silence", subtext: "Just being with myself", element: "air" },
      { key: "d", text: "In the thrill of chasing", subtext: "A big dream", element: "earth" }
    ]
  },
  {
    id: 3,
    title: "The Shadow",
    hindi: "Pachtava",
    question: "Every soul carries a little weight from the past. If you could let go of one regret today, what would it be?",
    keywords: ["soul", "regret"],
    options: [
      { key: "a", text: "I wish I had taken", subtext: "That one big risk", element: "fire" },
      { key: "b", text: "I wish I had given more time", subtext: "To the people I love", element: "water" },
      { key: "c", text: "I wish I had traveled", subtext: "To the places my heart wanted", element: "air" },
      { key: "d", text: "I wish I had listened", subtext: "To my soul instead of the world", element: "earth" }
    ]
  },
  {
    id: 4,
    title: "The Mirror",
    hindi: "Sach",
    question: "When you look in the mirror after a long day, who do you see?",
    keywords: ["mirror"],
    options: [
      { key: "a", text: "A Warrior", subtext: "Who never gives up", element: "fire" },
      { key: "b", text: "A Healer", subtext: "Who carries everyone's pain", element: "water" },
      { key: "c", text: "A Seeker", subtext: "Who is still finding their way", element: "air" },
      { key: "d", text: "A Leader", subtext: "Who is tired of being strong", element: "earth" }
    ]
  },
  {
    id: 5,
    title: "The Table",
    hindi: "Umeed",
    question: "When you sit at a table with 5 strangers, what are you really looking for?",
    keywords: ["table", "strangers"],
    options: [
      { key: "a", text: "To learn something", subtext: "I never knew before", element: "fire" },
      { key: "b", text: "To find someone who understands", subtext: "Me without judgment", element: "water" },
      { key: "c", text: "To find a new friend", subtext: "Or a partner for my journey", element: "air" },
      { key: "d", text: "Just a peaceful evening", subtext: "And a real conversation", element: "earth" }
    ]
  }
];

// Professional Tribe Questions (10 questions)
const TRIBE_QUESTIONS = [
  {
    id: 1,
    question: "It's Monday morning. Your calendar shows back-to-back meetings. What's your move?",
    options: [
      { key: "a", text: "Energized! Time to connect", trait: "connector" },
      { key: "b", text: "Block focus time first", trait: "thinker" },
      { key: "c", text: "Strategic meetings only", trait: "maverick" },
      { key: "d", text: "People meetings first", trait: "leader" }
    ]
  },
  {
    id: 2,
    question: "At a professional dinner, you're most likely to...",
    options: [
      { key: "a", text: "Share bold, challenging ideas", trait: "maverick" },
      { key: "b", text: "Listen and ask deep questions", trait: "thinker" },
      { key: "c", text: "Connect people at the table", trait: "connector" },
      { key: "d", text: "Guide with calm presence", trait: "leader" }
    ]
  },
  {
    id: 3,
    question: "Your ideal mentor is someone who...",
    options: [
      { key: "a", text: "Disrupted their industry", trait: "maverick" },
      { key: "b", text: "Has deep philosophical insights", trait: "thinker" },
      { key: "c", text: "Opens doors effortlessly", trait: "connector" },
      { key: "d", text: "Led teams through change", trait: "leader" }
    ]
  },
  {
    id: 4,
    question: "At a career crossroads, you typically...",
    options: [
      { key: "a", text: "Take the exciting path", trait: "maverick" },
      { key: "b", text: "Analyze all data points", trait: "thinker" },
      { key: "c", text: "Consult your network", trait: "connector" },
      { key: "d", text: "Trust your instincts", trait: "leader" }
    ]
  },
  {
    id: 5,
    question: "At work, you're most proud of...",
    options: [
      { key: "a", text: "Your innovations", trait: "maverick" },
      { key: "b", text: "Your quality work", trait: "thinker" },
      { key: "c", text: "Your relationships", trait: "connector" },
      { key: "d", text: "People you've inspired", trait: "leader" }
    ]
  },
  {
    id: 6,
    question: "Colleagues describe you as...",
    options: [
      { key: "a", text: "The disruptor", trait: "maverick" },
      { key: "b", text: "The expert", trait: "thinker" },
      { key: "c", text: "The connector", trait: "connector" },
      { key: "d", text: "The anchor", trait: "leader" }
    ]
  },
  {
    id: 7,
    question: "Your networking style is...",
    options: [
      { key: "a", text: "Bold and memorable", trait: "maverick" },
      { key: "b", text: "Deep conversations", trait: "thinker" },
      { key: "c", text: "Working the room", trait: "connector" },
      { key: "d", text: "Drawing people in", trait: "leader" }
    ]
  },
  {
    id: 8,
    question: "What energizes you most?",
    options: [
      { key: "a", text: "Unsolved problems", trait: "maverick" },
      { key: "b", text: "Mastering complexity", trait: "thinker" },
      { key: "c", text: "Bringing people together", trait: "connector" },
      { key: "d", text: "Team success", trait: "leader" }
    ]
  },
  {
    id: 9,
    question: "In conflict, you tend to...",
    options: [
      { key: "a", text: "Challenge directly", trait: "maverick" },
      { key: "b", text: "Analyze root cause", trait: "thinker" },
      { key: "c", text: "Find common ground", trait: "connector" },
      { key: "d", text: "Guide to consensus", trait: "leader" }
    ]
  },
  {
    id: 10,
    question: "Your dream community has...",
    options: [
      { key: "a", text: "Innovators & founders", trait: "maverick" },
      { key: "b", text: "Deep thinkers", trait: "thinker" },
      { key: "c", text: "Diverse professionals", trait: "connector" },
      { key: "d", text: "Leaders & mentors", trait: "leader" }
    ]
  }
];

// Soul Elements
const SOUL_ELEMENTS = {
  fire: {
    name: "Fire",
    hindi: "अग्नि",
    color: "from-orange-500 via-red-500 to-yellow-500",
    glow: "rgba(249, 115, 22, 0.6)",
    description: "You burn with passion and courage. Your spirit ignites others.",
    traits: ["Passionate", "Courageous", "Dynamic", "Transformative"]
  },
  water: {
    name: "Water", 
    hindi: "जल",
    color: "from-cyan-400 via-blue-500 to-indigo-500",
    glow: "rgba(34, 211, 238, 0.6)",
    description: "You flow with empathy and depth. Your soul heals and nurtures.",
    traits: ["Empathetic", "Intuitive", "Adaptable", "Nurturing"]
  },
  air: {
    name: "Air",
    hindi: "वायु", 
    color: "from-slate-300 via-gray-400 to-slate-500",
    glow: "rgba(203, 213, 225, 0.6)",
    description: "You move with curiosity and freedom. Your mind seeks endless horizons.",
    traits: ["Curious", "Free-spirited", "Intellectual", "Communicative"]
  },
  earth: {
    name: "Earth",
    hindi: "पृथ्वी",
    color: "from-emerald-500 via-green-600 to-teal-600",
    glow: "rgba(16, 185, 129, 0.6)",
    description: "You stand with strength and vision. Your presence grounds others.",
    traits: ["Grounded", "Reliable", "Patient", "Visionary"]
  }
};

// Tribe Types
const TRIBE_TYPES = {
  maverick: { name: "The Tech Maverick", emoji: "🚀", color: "from-teal-400 to-teal-600", description: "You're a disruptor at heart.", strengths: ["Innovation", "Risk-taking", "Vision"], networkStyle: "Bold connections", idealTable: "Founders Dinner" },
  thinker: { name: "The Zen Leader", emoji: "🧘", color: "from-slate-400 to-slate-600", description: "You bring calm depth to chaos.", strengths: ["Deep expertise", "Strategy", "Wisdom"], networkStyle: "Deep conversations", idealTable: "Thinkers Roundtable" },
  connector: { name: "The Social Catalyst", emoji: "🔗", color: "from-amber-400 to-amber-600", description: "You're the glue that builds communities.", strengths: ["Relationships", "Empathy", "Generosity"], networkStyle: "Warm introductions", idealTable: "Connectors Brunch" },
  leader: { name: "The Grounded Leader", emoji: "🌳", color: "from-teal-500 to-emerald-600", description: "You lead with presence, not position.", strengths: ["People development", "Trust", "Vision"], networkStyle: "Mentorship", idealTable: "Leadership Circle" }
};

const VibeMatch = () => {
  const navigate = useNavigate();
  
  const [phase, setPhase] = useState('soul');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  
  // Soul questions state
  const [soulIndex, setSoulIndex] = useState(0);
  const [soulAnswers, setSoulAnswers] = useState([]);
  const [selectedSoul, setSelectedSoul] = useState(null);
  
  // Soul element state
  const [soulElement, setSoulElement] = useState(null);
  
  // Lead form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Vibe match state
  const [vibeIndex, setVibeIndex] = useState(0);
  const [vibeAnswers, setVibeAnswers] = useState([]);
  const [selectedVibe, setSelectedVibe] = useState(null);
  
  const [tribeResult, setTribeResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Slow dissolve transition
  const dissolveTransition = (callback, nextPhase) => {
    setIsFadingOut(true);
    setTimeout(() => {
      if (callback) callback();
      if (nextPhase) setPhase(nextPhase);
      setIsFadingOut(false);
    }, 800);
  };

  // Handle Back navigation
  const handleBack = () => {
    if (phase === 'soul') {
      if (soulIndex > 0) {
        setSoulIndex(soulIndex - 1);
        setSoulAnswers(soulAnswers.slice(0, -1));
        setSelectedSoul(null);
      } else {
        navigate('/');
      }
    } else if (phase === 'soul-element') {
      setSoulIndex(SOUL_QUESTIONS.length - 1);
      setSoulAnswers(soulAnswers.slice(0, -1));
      setPhase('soul');
    } else if (phase === 'lead-form') {
      setPhase('soul-element');
    } else if (phase === 'vibe-match') {
      if (vibeIndex > 0) {
        setVibeIndex(vibeIndex - 1);
        setVibeAnswers(vibeAnswers.slice(0, -1));
        setSelectedVibe(null);
      } else {
        setPhase('lead-form');
      }
    }
  };

  // Navigation Component
  const NavControls = () => (
    <div className="absolute top-6 left-6 z-20 flex items-center gap-4">
      <button
        onClick={handleBack}
        className="group flex items-center gap-2 text-cream/40 hover:text-cream/80 transition-colors duration-300"
        data-testid="nav-back"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm tracking-wider">Back</span>
      </button>
      <span className="text-cream/20">|</span>
      <button
        onClick={() => navigate('/')}
        className="group flex items-center gap-2 text-cream/40 hover:text-cream/80 transition-colors duration-300"
        data-testid="nav-home"
      >
        <Home className="w-4 h-4" />
        <span className="text-sm tracking-wider">Home</span>
      </button>
    </div>
  );

  const validateEmail = (emailValue) => {
    if (!emailValue.includes('@')) return 'Please enter a valid email address';
    const domain = emailValue.split('@')[1]?.toLowerCase();
    if (!domain || !VALID_EMAIL_DOMAINS.includes(domain)) return 'Please use Gmail, Yahoo, Outlook, or iCloud';
    return '';
  };

  // Handle Soul question selection
  const handleSoulSelect = (option) => {
    setSelectedSoul(option);
    
    setTimeout(() => {
      const newAnswers = [...soulAnswers, option];
      setSoulAnswers(newAnswers);
      
      if (soulIndex < SOUL_QUESTIONS.length - 1) {
        dissolveTransition(() => {
          setSoulIndex(soulIndex + 1);
          setSelectedSoul(null);
        });
      } else {
        // Calculate soul element
        const elementCounts = { fire: 0, water: 0, air: 0, earth: 0 };
        newAnswers.forEach(a => { if (a.element) elementCounts[a.element]++; });
        const dominant = Object.entries(elementCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0];
        setSoulElement(SOUL_ELEMENTS[dominant]);
        dissolveTransition(null, 'soul-element');
      }
    }, 600);
  };

  const handleLeadSubmit = async () => {
    const error = validateEmail(email);
    if (error) { setEmailError(error); return; }
    if (!name.trim()) return;
    setEmailError('');
    
    try {
      const response = await fetch(`${API}/vibe-match/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() })
      });
      const data = await response.json();
      localStorage.setItem('heysolo_lead', JSON.stringify({ name, email, lead_id: data.lead_id }));
    } catch (error) {
      localStorage.setItem('heysolo_lead', JSON.stringify({ name, email, lead_id: 'local_' + Date.now() }));
    }
    
    dissolveTransition(null, 'vibe-match');
  };

  const handleVibeSelect = (option) => {
    setSelectedVibe(option);
    
    setTimeout(() => {
      const newAnswers = [...vibeAnswers, option];
      setVibeAnswers(newAnswers);
      
      if (vibeIndex < TRIBE_QUESTIONS.length - 1) {
        dissolveTransition(() => {
          setVibeIndex(vibeIndex + 1);
          setSelectedVibe(null);
        });
      } else {
        calculateFinalResult(newAnswers);
      }
    }, 600);
  };

  const calculateFinalResult = async (finalAnswers) => {
    setIsSubmitting(true);
    const traitCounts = { maverick: 0, thinker: 0, connector: 0, leader: 0 };
    finalAnswers.forEach(a => { if (a.trait) traitCounts[a.trait]++; });
    const dominantTrait = Object.entries(traitCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0];
    const result = TRIBE_TYPES[dominantTrait];
    
    const leadData = JSON.parse(localStorage.getItem('heysolo_lead') || '{}');
    try {
      await fetch(`${API}/vibe-match/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: leadData.lead_id, name: leadData.name, email: leadData.email, soul_element: soulElement?.name, tribe_type: dominantTrait })
      });
    } catch (e) {}
    
    setTribeResult({ ...result, type: dominantTrait });
    dissolveTransition(null, 'result');
    setIsSubmitting(false);
  };

  // Render question text with italic keywords
  const renderQuestion = (question, keywords) => {
    let text = question;
    keywords.forEach(keyword => {
      const regex = new RegExp(`(${keyword})`, 'gi');
      text = text.replace(regex, `<em class="text-champagne/90">$1</em>`);
    });
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  // Video Background
  const VideoBackground = () => (
    <div className="fixed inset-0 z-0">
      <video autoPlay muted loop playsInline className="w-full h-full object-cover" style={{ filter: 'saturate(0.7) contrast(1.1)' }}>
        <source src="https://videos.pexels.com/video-files/5529079/5529079-hd_1920_1080_30fps.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/75" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
    </div>
  );

  // PHASE 1: Soul Questions - Emotional Cinematic
  if (phase === 'soul') {
    const currentQ = SOUL_QUESTIONS[soulIndex];
    
    return (
      <div className="fixed inset-0 overflow-hidden">
        <VideoBackground />
        <NavControls />
        
        <div className={`relative z-10 h-full flex flex-col items-center justify-center px-6 transition-all duration-700 ease-out ${isFadingOut ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          
          {/* Progress dots */}
          <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-3">
            {SOUL_QUESTIONS.map((_, idx) => (
              <div key={idx} className={`h-1 rounded-full transition-all duration-500 ${idx === soulIndex ? 'w-8 bg-champagne' : idx < soulIndex ? 'w-2 bg-champagne/50' : 'w-2 bg-cream/20'}`} />
            ))}
          </div>

          {/* Main Question - Hero, extra large, italic keywords */}
          <div className="max-w-5xl text-center mb-16 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-cream leading-snug font-light" style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 4px 40px rgba(0,0,0,0.5)' }}>
              {renderQuestion(currentQ.question, currentQ.keywords)}
            </h1>
          </div>

          {/* Options - 2x2 Grid with dark background for readability */}
          <div className="w-full max-w-4xl grid grid-cols-2 gap-6 md:gap-10 px-4">
            {currentQ.options.map((option) => (
              <button
                key={option.key}
                onClick={() => handleSoulSelect(option)}
                disabled={selectedSoul !== null}
                className={`group relative py-8 md:py-10 px-6 text-center transition-all duration-500 rounded-sm backdrop-blur-md ${
                  selectedSoul?.key === option.key 
                    ? 'bg-champagne/20 border border-champagne/60' 
                    : selectedSoul !== null 
                      ? 'opacity-20 bg-black/40' 
                      : 'bg-black/50 border border-cream/20 hover:border-champagne/50 hover:bg-black/60'
                }`}
                style={{ boxShadow: selectedSoul?.key === option.key ? '0 0 40px rgba(212, 175, 55, 0.3)' : '0 4px 30px rgba(0,0,0,0.3)' }}
                data-testid={`soul-option-${option.key}`}
              >
                <span className={`block text-lg md:text-xl font-medium transition-colors duration-300 ${
                  selectedSoul?.key === option.key ? 'text-champagne' : 'text-cream group-hover:text-champagne'
                }`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  {option.text}
                </span>
                <span className={`block text-sm md:text-base mt-2 transition-colors duration-300 ${
                  selectedSoul?.key === option.key ? 'text-cream/80' : 'text-cream/60 group-hover:text-cream/70'
                }`}>
                  {option.subtext}
                </span>
                
                {/* Soft glow on hover */}
                <div className={`absolute inset-0 rounded-sm transition-opacity duration-500 pointer-events-none ${
                  selectedSoul?.key === option.key ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`} style={{ boxShadow: 'inset 0 0 30px rgba(212, 175, 55, 0.15)' }} />
              </button>
            ))}
          </div>

          {/* Brand watermark - more visible */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <span className="text-cream/50 text-sm tracking-[0.4em] font-medium" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>HEYSOLO.AI</span>
          </div>
        </div>
      </div>
    );
  }

  // PHASE 2: Soul Element - Glowing Orb
  if (phase === 'soul-element' && soulElement) {
    return (
      <div className="fixed inset-0 overflow-hidden">
        <VideoBackground />
        <NavControls />
        
        <div className={`relative z-10 h-full flex flex-col items-center justify-center px-6 transition-all duration-700 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}>
          
          {/* Title */}
          <p className="text-champagne/40 text-xs tracking-[0.5em] uppercase mb-2">Your Soul Element</p>
          <p className="text-cream/30 text-sm mb-12">The essence that defines you</p>

          {/* Glowing Orb */}
          <div className="relative mb-12">
            {/* Outer glow rings */}
            <div className="absolute inset-0 -m-8 rounded-full animate-pulse" style={{ background: `radial-gradient(circle, ${soulElement.glow} 0%, transparent 70%)`, filter: 'blur(20px)' }} />
            <div className="absolute inset-0 -m-16 rounded-full animate-pulse" style={{ background: `radial-gradient(circle, ${soulElement.glow} 0%, transparent 60%)`, filter: 'blur(40px)', animationDelay: '0.5s' }} />
            
            {/* Main orb */}
            <div className={`relative w-40 h-40 md:w-52 md:h-52 rounded-full bg-gradient-to-br ${soulElement.color} flex items-center justify-center`} style={{ boxShadow: `0 0 60px ${soulElement.glow}, inset 0 0 40px rgba(255,255,255,0.2)` }}>
              <div className="text-center">
                <span className="text-4xl md:text-5xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
                  {soulElement.name}
                </span>
                <p className="text-white/60 text-lg mt-1">{soulElement.hindi}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-cream/70 text-lg md:text-xl text-center max-w-lg mb-8 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            "{soulElement.description}"
          </p>

          {/* Traits */}
          <div className="flex flex-wrap justify-center gap-3 mb-16">
            {soulElement.traits.map((trait, idx) => (
              <span key={idx} className="px-4 py-2 text-sm text-cream/60 border border-cream/20 rounded-full">
                {trait}
              </span>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-cream text-xl md:text-2xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to discover your <em className="text-champagne">Vibe</em>?
            </p>
            <p className="text-cream/40 text-sm mb-8">Find your professional tribe</p>

            <button
              onClick={() => dissolveTransition(null, 'lead-form')}
              className="group relative px-14 py-5 text-cream border border-champagne/50 hover:border-champagne transition-all duration-500"
              data-testid="discover-vibe-btn"
            >
              <span className="text-sm tracking-[0.25em] uppercase">Yes, Continue</span>
              <div className="absolute inset-0 bg-champagne/0 group-hover:bg-champagne/10 transition-all duration-500" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PHASE 3: Lead Form
  if (phase === 'lead-form') {
    return (
      <div className="fixed inset-0 overflow-hidden">
        <VideoBackground />
        <NavControls />
        
        <div className={`relative z-10 h-full flex flex-col items-center justify-center px-6 transition-all duration-700 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}>
          
          <div className="w-full max-w-md">
            <div className="text-center mb-14">
              <p className="text-champagne/30 text-xs tracking-[0.4em] uppercase mb-4">Before we continue</p>
              <h2 className="text-3xl md:text-4xl text-cream" style={{ fontFamily: "'Playfair Display', serif" }}>
                Who's on this <em className="text-champagne/90">journey</em>?
              </h2>
            </div>

            <div className="space-y-10">
              <div>
                <label className="text-cream/40 text-xs tracking-[0.2em] uppercase block mb-4">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full bg-transparent border-b border-cream/20 focus:border-champagne/60 text-cream text-lg py-3 outline-none transition-colors placeholder:text-cream/20"
                  data-testid="lead-name"
                />
              </div>
              <div>
                <label className="text-cream/40 text-xs tracking-[0.2em] uppercase block mb-4">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                  placeholder="you@gmail.com"
                  className={`w-full bg-transparent border-b ${emailError ? 'border-red-400' : 'border-cream/20 focus:border-champagne/60'} text-cream text-lg py-3 outline-none transition-colors placeholder:text-cream/20`}
                  data-testid="lead-email"
                />
                {emailError && <p className="text-red-400 text-sm mt-3">{emailError}</p>}
                <p className="text-cream/20 text-xs mt-3">Gmail, Yahoo, Outlook, iCloud accepted</p>
              </div>
            </div>

            <div className="mt-14 text-center">
              <button
                onClick={handleLeadSubmit}
                disabled={!name.trim() || !email.includes('@')}
                className="group relative px-16 py-5 text-cream border border-champagne/50 hover:border-champagne disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-500"
                data-testid="lead-submit"
              >
                <span className="text-sm tracking-[0.25em] uppercase">Continue</span>
                <div className="absolute inset-0 bg-champagne/0 group-hover:bg-champagne/10 transition-all duration-500" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PHASE 4: Vibe Match
  if (phase === 'vibe-match') {
    const currentQ = TRIBE_QUESTIONS[vibeIndex];
    
    return (
      <div className="fixed inset-0 overflow-hidden">
        <VideoBackground />
        <NavControls />
        
        <div className={`relative z-10 h-full flex flex-col items-center justify-center px-6 transition-all duration-700 ${isFadingOut ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          
          {/* Progress */}
          <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-2">
            {TRIBE_QUESTIONS.map((_, idx) => (
              <div key={idx} className={`h-1 rounded-full transition-all duration-300 ${idx === vibeIndex ? 'w-6 bg-teal-400' : idx < vibeIndex ? 'w-1.5 bg-teal-400/50' : 'w-1.5 bg-cream/20'}`} />
            ))}
          </div>

          <div className="absolute top-16 left-1/2 -translate-x-1/2">
            <p className="text-teal-400/30 text-xs tracking-[0.4em] uppercase">Professional Insight</p>
          </div>

          {/* Question */}
          <div className="max-w-4xl text-center mb-16 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-cream leading-snug font-light" style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
              {currentQ.question}
            </h1>
          </div>

          {/* Options - 2x2 Grid */}
          <div className="w-full max-w-3xl grid grid-cols-2 gap-5 md:gap-8 px-4">
            {currentQ.options.map((option) => (
              <button
                key={option.key}
                onClick={() => handleVibeSelect(option)}
                disabled={selectedVibe !== null || isSubmitting}
                className={`group py-6 md:py-8 px-4 text-center transition-all duration-500 border rounded-sm ${
                  selectedVibe?.key === option.key 
                    ? 'bg-teal-500/15 border-teal-400/50' 
                    : selectedVibe !== null 
                      ? 'opacity-20 border-cream/10' 
                      : 'border-cream/10 hover:border-teal-400/40 hover:bg-teal-500/5'
                }`}
                data-testid={`vibe-option-${option.key}`}
              >
                <span className={`text-base md:text-lg transition-colors duration-300 ${
                  selectedVibe?.key === option.key ? 'text-teal-400' : 'text-cream/80 group-hover:text-teal-400'
                }`}>
                  {option.text}
                </span>
              </button>
            ))}
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <span className="text-cream/20 text-sm">{name}</span>
          </div>
        </div>
      </div>
    );
  }

  // PHASE 5: Result
  if (phase === 'result' && tribeResult) {
    return (
      <div className="fixed inset-0 overflow-hidden">
        <VideoBackground />
        
        {/* Home button only on result page */}
        <div className="absolute top-6 left-6 z-20">
          <button
            onClick={() => navigate('/')}
            className="group flex items-center gap-2 text-cream/40 hover:text-cream/80 transition-colors duration-300"
            data-testid="nav-home"
          >
            <Home className="w-4 h-4" />
            <span className="text-sm tracking-wider">Home</span>
          </button>
        </div>
        
        <div className={`relative z-10 h-full flex flex-col items-center justify-center px-6 transition-all duration-700 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}>
          
          {/* Soul Element Badge */}
          {soulElement && (
            <p className="text-champagne/50 text-sm tracking-[0.2em] uppercase mb-6">
              Soul Element: {soulElement.name}
            </p>
          )}

          {/* Emoji */}
          <div className={`w-24 h-24 md:w-28 md:h-28 rounded-3xl bg-gradient-to-br ${tribeResult.color} flex items-center justify-center mb-6`} style={{ boxShadow: '0 0 40px rgba(20, 184, 166, 0.3)' }}>
            <span className="text-5xl">{tribeResult.emoji}</span>
          </div>

          {/* Tribe Name */}
          <p className="text-teal-400/60 text-xs tracking-[0.3em] uppercase mb-2">Your Tribe</p>
          <h1 className="text-3xl md:text-5xl text-cream mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {tribeResult.name}
          </h1>

          <p className="text-cream/50 text-lg md:text-xl text-center max-w-md mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            {tribeResult.description}
          </p>

          {/* Strengths */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {tribeResult.strengths.map((s, i) => (
              <span key={i} className="px-4 py-2 text-sm text-teal-300 border border-teal-400/30 rounded-full">{s}</span>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/#marketplace')}
            className="px-10 py-4 bg-teal-600/80 hover:bg-teal-600 text-cream rounded-full transition-all duration-300"
          >
            <span className="text-sm tracking-[0.15em] uppercase flex items-center gap-2">
              Meet Your Tribe <ArrowRight className="w-4 h-4" />
            </span>
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default VibeMatch;
